/**
 * Filtros globais: clientes, produto ativo (abas por produto) e multi-produto (painel/auditoria/fluxo).
 */

import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
const STORAGE_KEY = 'vover_admin_product_id';
const MULTI_STORAGE_KEY = 'vover_admin_product_ids';
/** Chaves legadas (pré multi-produto productId) — migradas uma vez no primeiro load. */
const LEGACY_STORAGE_KEY = 'vover_admin_application_id';
const LEGACY_MULTI_STORAGE_KEY = 'vover_admin_application_ids';

interface FilterContextType {
  selectedClients: string[];
  setSelectedClients: (clients: string[]) => void;
  productId: string;
  setProductId: (id: string) => void;
  /** @deprecated use productId — alias para bundles antigos em cache */
  applicationId: string;
  /** @deprecated use setProductId */
  setApplicationId: (id: string) => void;
  /** Vazio = todos os produtos (nas páginas multi-produto). */
  selectedProductIds: string[];
  setSelectedProductIds: (ids: string[]) => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

function readStoredProductId(): string {
  if (typeof window === 'undefined') return '';
  const current = localStorage.getItem(STORAGE_KEY)?.trim();
  if (current) return current;
  const legacy = localStorage.getItem(LEGACY_STORAGE_KEY)?.trim();
  if (legacy) {
    localStorage.setItem(STORAGE_KEY, legacy);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    return legacy;
  }
  return '';
}

function readStoredProductIds(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    let raw = localStorage.getItem(MULTI_STORAGE_KEY);
    if (!raw) {
      raw = localStorage.getItem(LEGACY_MULTI_STORAGE_KEY);
      if (raw) {
        localStorage.setItem(MULTI_STORAGE_KEY, raw);
        localStorage.removeItem(LEGACY_MULTI_STORAGE_KEY);
      }
    }
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x: unknown) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export const FilterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [productId, setProductIdState] = useState<string>(readStoredProductId);
  const [selectedProductIds, setSelectedProductIdsState] = useState<string[]>(readStoredProductIds);

  const setProductId = useCallback((id: string) => {
    const next = id.trim();
    setProductIdState(next);
    if (typeof window !== 'undefined') {
      if (next) localStorage.setItem(STORAGE_KEY, next);
      else localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const setSelectedProductIds = useCallback((ids: string[]) => {
    setSelectedProductIdsState(ids);
    if (typeof window !== 'undefined') {
      localStorage.setItem(MULTI_STORAGE_KEY, JSON.stringify(ids));
    }
  }, []);

  return (
    <FilterContext.Provider
      value={{
        selectedClients,
        setSelectedClients,
        productId,
        setProductId,
        applicationId: productId,
        setApplicationId: setProductId,
        selectedProductIds,
        setSelectedProductIds
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilters must be used within FilterProvider');
  }
  return context;
};
